import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageWrapper } from "@/components/PageWrapper";
import { PlayerPickerDialog } from "@/components/PlayerPicker/PlayerPickerDialog";
import { ShareFlowHost } from "@/components/ShareDesignPost/ShareFlowHost";
import { useShareFlow } from "@/components/ShareDesignPost/ShareFlowProvider";
import { Button, confirmDialog, SimpleSelect, Tabs } from "@/components/ui";
import { isApiError } from "@/services/bedrock/apiError";
import { DesignPackageDetail, PackageStatus } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { designEditorUrl, URLS } from "@/utils/routes";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { BackgroundsTray } from "./components/BackgroundsTray";
import { EditorHeader, EditorMode } from "./components/EditorHeader";
import { EditorSkeleton } from "./components/EditorSkeleton";
import { GenerationProgress } from "./components/GenerationProgress";
import { PackageErrorState } from "./components/PackageErrorState";
import { PerformanceSection } from "./components/PerformanceSection";
import { PreviewPanel } from "./components/PreviewPanel";
import { PublishDialog } from "./components/PublishDialog";
import { READ_ONLY_NOTICE, ReadOnlyNotice } from "./components/ReadOnlyNotice";
import { ComposerState, RefinementComposer, SendOptions } from "./components/RefinementComposer";
import { PendingRequest, RefinementThread } from "./components/RefinementThread";
import { inFlightReasonFor } from "./components/DisabledReason";
import { SourcePanel } from "./components/SourcePanel";
import { TracePanel } from "./components/TracePanel";
import { UnpublishDialog } from "./components/UnpublishDialog";
import { EditorPanel, readEditorParams, writeEditorParams } from "./editorSearchParams";
import { DesignStudioHelpDialog } from "./help/DesignStudioHelpDialog";
import type { HelpTopic } from "./help/HelpDialog";
import { useActivity } from "./hooks/useActivity";
import { useDesignPackage } from "./hooks/useDesignPackage";
import { useDesignPackageChannel } from "./hooks/useDesignPackageChannel";
import { inFlightMessage, useDesignPackageMutations } from "./hooks/useDesignPackageMutations";
import { useEditorThemes } from "./hooks/useEditorThemes";
import { usePresence } from "./hooks/usePresence";
import { editRequestValues } from "./NewDesignPage";
import { DesignStudioScopeContext } from "./DesignStudioScope";
import { buildThread, filterThread, ThreadFilter } from "./thread";
import { isSelfActor, useViewer, Viewer, viewerActor } from "./viewer";

export function editorModeFor(
  pkg: Pick<DesignPackageDetail, "archived" | "isGlobal"> | undefined,
  access: { isAdmin: boolean; isReadOnly: boolean }
): EditorMode {
  if (pkg?.archived) return "archived";
  if (pkg?.isGlobal && !access.isAdmin) return "globalForCoach";
  if (access.isReadOnly) return "readOnly";
  return "owner";
}

/** `?slide` when it names an item; else the first slide with a pending version; else the first slide. */
export function selectedItemIdFor(pkg: Pick<DesignPackageDetail, "items">, slideParam: number | null): number | null {
  const items = [...pkg.items].sort((a, b) => a.position - b.position);
  if (slideParam != null && items.some(i => i.id === slideParam)) return slideParam;
  return (items.find(i => i.candidateTemplate) ?? items[0])?.id ?? null;
}

export function composerStateFor(
  pkg: DesignPackageDetail,
  mode: EditorMode,
  sending: boolean,
  viewer: Viewer
): ComposerState {
  if (mode === "readOnly") return { kind: "disabled_read_only" };
  if (mode === "archived") return { kind: "disabled_archived" };
  if (mode === "globalForCoach") return { kind: "disabled_global" };
  if (sending) return { kind: "sending" };
  if (pkg.inFlight?.kind === "refine") {
    return {
      kind: "disabled_in_flight",
      actor: pkg.inFlight.actor,
      itemIds: pkg.inFlight.itemIds,
      isSelf: isSelfActor(pkg.inFlight.actor, viewer),
    };
  }
  return { kind: "idle", candidatePending: pkg.items.some(i => i.candidateTemplate) };
}

function usePackageIdParam(): number | null {
  const { packageId } = useParams<{ packageId: string }>();
  const parsedId = Number(packageId);
  return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
}

/** The editor, with the share composer a coach uses to post this design for a player. */
export function DesignEditorPage(): JSX.Element {
  const id = usePackageIdParam();
  const queryClient = useQueryClient();
  // The stats come from the channel's rendered_for_player message, so only the design and its changes refresh here.
  const handlePosted = () => {
    if (id == null) return;
    queryClient.invalidateQueries({ queryKey: designKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: designKeys.activity(id) });
  };
  return (
    <ShareFlowHost playerId={null} onBehalfOf onPosted={handlePosted}>
      <DesignEditorContent />
    </ShareFlowHost>
  );
}

function DesignEditorContent(): JSX.Element {
  const id = usePackageIdParam();
  const history = useHistory();
  const location = useLocation();
  const adminScope = React.useContext(DesignStudioScopeContext);
  const access = useDynamicPromptsAccess();
  const { isAdmin, isImpersonating } = useSocialCoachSession();
  const viewer = useViewer();
  // Impersonating admins act as the coach, who never sees template code.
  const canOpenSource = isAdmin && !isImpersonating;

  const params = readEditorParams(location.search);
  const { options: themeOptions, isLoading: themesLoading } = useEditorThemes();
  // Until the themes load, trust the URL so a restored theme doesn't flash back to the base preview.
  const themeId =
    params.theme != null && (themesLoading || themeOptions.some(o => o.theme.id === params.theme))
      ? params.theme
      : null;

  const { pkg, progress, isLoading, error } = useDesignPackage(id);
  const { connection } = useDesignPackageChannel(id, { presence: true, pollThemeId: themeId });
  const { viewers } = usePresence(id);
  const { retry, rename, refine, publish, unpublish, archive, restore, duplicate, approve, discard, busy } =
    useDesignPackageMutations(id);
  const activity = useActivity(pkg?.status === "completed" ? id : null);
  const [helpTopic, setHelpTopic] = React.useState<HelpTopic | null>(null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [unpublishOpen, setUnpublishOpen] = React.useState(false);
  const [traceOpen, setTraceOpen] = React.useState(false);
  const [sourceOpen, setSourceOpen] = React.useState(false);
  const [threadFilter, setThreadFilter] = React.useState<ThreadFilter | null>(null);
  const [pendingRequest, setPendingRequest] = React.useState<PendingRequest | null>(null);
  const [prefill, setPrefill] = React.useState<{ text: string; nonce: number } | undefined>();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const shareFlow = useShareFlow();
  const threadScrollRef = React.useRef<HTMLDivElement>(null);
  const threadScrollTop = React.useRef<number | null>(null);
  const panelParam = params.panel;
  React.useLayoutEffect(() => {
    if (panelParam !== "activity" || threadScrollTop.current == null || !threadScrollRef.current) return;
    threadScrollRef.current.scrollTop = threadScrollTop.current;
    threadScrollTop.current = null;
  }, [panelParam]);

  const mode = editorModeFor(pkg, access);
  const canEdit = access.canEdit && mode === "owner";
  const listUrl = adminScope ? URLS.admin.designStudio : URLS.coach.designStudio;
  const newDesignUrl = adminScope ? URLS.admin.adminNewDesign : URLS.coach.newDesign;

  const selectedItemId = pkg ? selectedItemIdFor(pkg, params.slide) : null;
  const multiSlide = (pkg?.items.length ?? 0) > 1;
  // Carousels open on the selected slide's changes; single slides have only one conversation.
  const filter: ThreadFilter = threadFilter ?? (multiSlide ? "slide" : "all");
  const thread = React.useMemo(
    () =>
      pkg
        ? filterThread(
            buildThread(activity.events, pkg, { everything: filter === "everything" }),
            filter,
            selectedItemId
          )
        : [],
    [activity.events, pkg, filter, selectedItemId]
  );

  const onRetry = async () => {
    if (id == null) return;
    try {
      await retry.mutateAsync(id);
    } catch {
      // The mutation hook already toasts the error.
    }
  };

  const onEditRequest = () => {
    if (pkg) history.push(newDesignUrl, editRequestValues(pkg));
  };

  const onPublish = async () => {
    if (!pkg) return;
    if (!pkg.isGlobal) {
      setPublishOpen(true);
      return;
    }
    const confirmed = await confirmDialog({
      title: "Publish this global template?",
      description: "Coaches will be able to find it in Design Studio and add it to their designs.",
      confirmLabel: "Publish",
      cancelLabel: "Cancel",
    });
    if (!confirmed) return;
    try {
      await publish.mutateAsync({});
    } catch (e) {
      if (isApiError(e, "not_completed")) toast.error("Finish generating before publishing.");
      else if (isApiError(e, "refine_in_progress")) {
        const actor = (e.details as { actor?: { name: string } } | undefined)?.actor;
        toast.error(actor ? inFlightMessage(actor.name) : "Wait for the current change to finish.");
      }
    }
  };

  const onDuplicate = async () => {
    if (id == null || !pkg) return;
    const coachId = pkg.isGlobal && adminScope?.scope === "coach" ? adminScope.coachId : null;
    try {
      const copy = await duplicate.mutateAsync(coachId != null ? { id, coachId } : id);
      toast.success(pkg.isGlobal ? "Added as a draft. Refine anything you like, then publish." : "Copy created.");
      history.push(designEditorUrl(copy.id, { admin: !!adminScope }), { justDuplicated: true });
    } catch {
      // The mutation hook already toasts the error.
    }
  };

  const onArchive = async () => {
    const confirmed = await confirmDialog({
      title: "Archive this design?",
      description:
        "It will be unpublished and hidden from your list. You can restore it later from the Archived filter.",
      confirmLabel: "Archive",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (confirmed) archive.mutate();
  };

  // The request shows in the thread at once, as "Working…", until its activity event arrives.
  const onSend = async (instructions: string, scope: "all" | number, { fromKept }: SendOptions) => {
    const item = scope === "all" ? null : pkg?.items.find(i => i.id === scope);
    setPendingRequest({
      instructions,
      scope: item ? { itemId: item.id, position: item.position } : "all",
      actor: viewerActor(viewer),
      at: new Date().toISOString(),
    });
    try {
      await refine.mutateAsync({
        instructions,
        itemId: scope === "all" ? undefined : scope,
        ...(fromKept ? { baseDesignTemplateId: null } : {}),
      });
    } finally {
      setPendingRequest(null);
    }
  };

  const renderReady = (detail: DesignPackageDetail) => {
    if (selectedItemId == null) return null;
    const selectedItem = detail.items.find(i => i.id === selectedItemId);
    // On-behalf posts can exist before a design is published, so live or scheduled posts alone also earn the tab.
    const hasPerformance = detail.publishedAt !== null || detail.postsCount > 0 || detail.scheduledPostsCount > 0;
    const panel: EditorPanel = hasPerformance ? params.panel : "activity";
    return (
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 overflow-y-auto lg:overflow-hidden">
        <section className="flex lg:flex-[3] min-w-0 flex-col gap-4 lg:min-h-0 lg:overflow-hidden pb-4">
          <PreviewPanel
            pkg={detail}
            selectedItemId={selectedItemId}
            onSelectItem={itemId => writeEditorParams(history, location, { slide: itemId })}
            themeId={themeId}
            onThemeChange={next => writeEditorParams(history, location, { theme: next })}
            mode={mode}
            onOpenHelp={topic => setHelpTopic(topic)}
          >
            {selectedItem && (
              <BackgroundsTray
                pkg={detail}
                item={selectedItem}
                themeId={themeId}
                mode={mode}
                onOpenHelp={topic => setHelpTopic(topic)}
              />
            )}
          </PreviewPanel>
        </section>

        {/* Full height on wide screens: the header and composer stay put and only the thread scrolls. */}
        <aside className="flex flex-col w-full lg:w-[380px] xl:w-[420px] shrink-0 min-h-[50dvh] lg:min-h-0 lg:overflow-hidden">
          <div
            className={
              hasPerformance
                ? "mb-3 flex shrink-0 items-end justify-between gap-2 border-b border-slate-200"
                : "flex shrink-0 items-center justify-between gap-2 pb-3"
            }
          >
            {hasPerformance ? (
              <Tabs
                value={panel}
                onValueChange={value => {
                  // Hiding the thread can reset its scroll, so remember where the coach was.
                  if (value === "performance") threadScrollTop.current = threadScrollRef.current?.scrollTop ?? null;
                  writeEditorParams(history, location, { panel: value as EditorPanel });
                }}
              >
                {/* The active tab's underline sits on the header's rule. */}
                <Tabs.List variant="line" className="h-9 gap-4 p-0">
                  {(["activity", "performance"] as const).map(value => (
                    <Tabs.Trigger
                      key={value}
                      value={value}
                      className="h-full flex-none rounded-none px-0.5 text-sm font-semibold text-slate-500 hover:text-slate-800 data-active:text-slate-950 after:bg-slate-900"
                    >
                      {value === "activity" ? "Activity" : "Performance"}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
              </Tabs>
            ) : (
              <div className="text-sm font-semibold text-slate-800">Activity</div>
            )}
            {panel === "activity" && (
              <div
                role="group"
                aria-label="Show changes"
                className={hasPerformance ? "pb-2 text-sm text-slate-600" : "text-sm text-slate-600"}
              >
                <SimpleSelect
                  items={
                    multiSlide
                      ? [
                          { value: "slide", label: "This slide" },
                          { value: "all", label: "All slides" },
                          { value: "everything", label: "Everything" },
                        ]
                      : [
                          { value: "all", label: "Changes" },
                          { value: "everything", label: "Everything" },
                        ]
                  }
                  value={filter}
                  onValueChange={value => setThreadFilter((value as ThreadFilter) ?? null)}
                />
              </div>
            )}
          </div>
          {panel === "performance" && (
            <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="performance-panel">
              <PerformanceSection pkg={detail} onPostForPlayer={canPostForPlayer ? onPostForPlayer : undefined} />
            </div>
          )}
          {/* Hidden, not unmounted, under Performance: an unmounted composer loses its draft and replays its last prefill. */}
          <div className="flex min-h-0 flex-1 flex-col" hidden={panel !== "activity"} data-testid="activity-panel">
            {/* A little room on the left and top, pulled back with margins, so focus outlines aren't clipped. */}
            <div
              ref={threadScrollRef}
              className="min-h-0 flex-1 lg:-mt-1 lg:-ml-1 lg:overflow-y-auto lg:pt-1 lg:pr-1 lg:pl-1"
              data-thread-scroll
            >
              <RefinementThread
                entries={thread}
                pkg={detail}
                selectedItemId={selectedItemId}
                onSelectItem={itemId => writeEditorParams(history, location, { slide: itemId })}
                onPrefill={text => setPrefill(current => ({ text, nonce: (current?.nonce ?? 0) + 1 }))}
                isLoadingMore={activity.isFetchingNextPage}
                hasMore={!!activity.hasNextPage}
                onLoadMore={() => void activity.fetchNextPage()}
                pending={pendingRequest}
                decisions={{
                  canDecide: canEdit,
                  disabledReason:
                    detail.inFlight?.kind === "refine" ? inFlightReasonFor(detail.inFlight, viewer) : null,
                  busy,
                  onKeep: itemId => approve.mutate(itemId),
                  onDiscard: itemId => discard.mutate(itemId),
                }}
              />
            </div>
            <div className="shrink-0 bg-white pt-3 pb-4">
              <RefinementComposer
                pkg={detail}
                selectedItemId={selectedItemId}
                state={composerStateFor(detail, mode, refine.isPending, viewer)}
                onSend={onSend}
                prefill={prefill}
              />
            </div>
          </div>
        </aside>
      </div>
    );
  };

  // Help's refine examples fill the composer only when a change can be sent now
  const canRefineNow =
    pkg?.status === "completed" && composerStateFor(pkg, mode, refine.isPending, viewer).kind === "idle";
  const pickRefineExample = (text: string) => {
    setPrefill(current => ({ text, nonce: (current?.nonce ?? 0) + 1 }));
    writeEditorParams(history, location, { panel: "activity" });
  };

  const renderBody = (status: PackageStatus, detail: DesignPackageDetail) => {
    switch (status) {
      case "queued":
      case "generating_images":
      case "generating_html":
      case "rendering_previews":
        return (
          <GenerationProgress
            pkg={detail}
            progress={progress}
            canEdit={canEdit}
            onBackToList={() => history.push(listUrl)}
          />
        );
      case "failed":
        return (
          <PackageErrorState
            pkg={detail}
            canEdit={canEdit}
            onRetry={onRetry}
            onEditRequest={onEditRequest}
            isRetrying={retry.isPending}
          />
        );
      case "completed":
        return renderReady(detail);
    }
  };

  // not_found and forbidden are the expected cases; with retries off, any other load failure lands here too.
  const unavailable =
    id == null || (!pkg && (isApiError(error, "not_found") || isApiError(error, "forbidden") || error != null));

  const centered = (content: React.ReactNode) => (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="max-w-3xl mx-auto py-10 flex flex-col gap-6">{content}</div>
    </div>
  );

  const renderContent = () => {
    if (unavailable) {
      return centered(
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <p className="text-lg font-semibold text-slate-700">This design isn't available.</p>
          <Button variant="outline" onClick={() => history.push(listUrl)}>
            Back to Design Studio
          </Button>
        </div>
      );
    }
    if (isLoading || !pkg) return <EditorSkeleton />;
    if (pkg.status === "completed") return renderBody(pkg.status, pkg);
    return centered(renderBody(pkg.status, pkg));
  };

  const ready = pkg?.status === "completed";
  const canPostForPlayer = access.canPostForPlayers && ready && !pkg?.archived;
  const onPostForPlayer = () => setPickerOpen(true);

  return (
    <PageWrapper fitViewport>
      <div className="flex flex-col h-full min-h-0">
        {!unavailable && (
          <EditorHeader
            pkg={pkg}
            connection={connection}
            mode={mode}
            viewers={viewers}
            inFlight={pkg?.inFlight}
            onOpenHelp={topic => setHelpTopic(topic)}
            onRename={ready ? name => rename.mutateAsync(name).then(() => undefined) : undefined}
            onPublish={ready ? () => void onPublish() : undefined}
            onUnpublish={ready ? () => setUnpublishOpen(true) : undefined}
            onDuplicate={ready ? () => void onDuplicate() : undefined}
            onArchive={ready ? () => void onArchive() : undefined}
            onRestore={pkg?.archived ? () => restore.mutate() : undefined}
            onPostForPlayer={canPostForPlayer ? onPostForPlayer : undefined}
            onOpenTrace={isAdmin ? () => setTraceOpen(true) : undefined}
            onOpenSource={canOpenSource && ready ? () => setSourceOpen(true) : undefined}
          />
        )}
        <div className="flex min-h-0 flex-1 flex-col px-4 pt-4 md:px-8">
          {mode === "readOnly" && pkg && <ReadOnlyNotice {...READ_ONLY_NOTICE} className="mb-4" />}
          {renderContent()}
        </div>
      </div>

      {pkg && ready && (
        <>
          <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} pkg={pkg} onPublished={() => undefined} />
          <UnpublishDialog
            open={unpublishOpen}
            onOpenChange={setUnpublishOpen}
            pkg={pkg}
            onConfirm={opts => unpublish.mutateAsync(opts).then(() => undefined)}
          />
        </>
      )}
      {pkg && canPostForPlayer && (
        <PlayerPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={player =>
            shareFlow.open({
              kind: "coach",
              source: "package",
              playerId: player.id,
              playerUserId: player.userId,
              designPackageId: pkg.id,
              promptId: pkg.publishedAt ? pkg.promptId : null,
            })
          }
        />
      )}
      {isAdmin && <TracePanel pkg={pkg} open={traceOpen} onOpenChange={setTraceOpen} />}
      {pkg && ready && canOpenSource && (
        <SourcePanel
          pkg={pkg}
          item={pkg.items.find(i => i.id === selectedItemId)}
          themeId={themeId}
          open={sourceOpen}
          onOpenChange={setSourceOpen}
        />
      )}
      <DesignStudioHelpDialog
        open={helpTopic !== null}
        topic={helpTopic ?? undefined}
        onOpenChange={open => !open && setHelpTopic(null)}
        briefAction={{ kind: "refine", onPick: canRefineNow ? pickRefineExample : undefined }}
      />
    </PageWrapper>
  );
}
